import { useState, useEffect, useCallback } from "react";
import { UserRound, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import TelegramLinkCard from "@/components/dashboard/TelegramLinkCard";
import ReferralPanel from "@/components/dashboard/ReferralPanel";
import CountryPicker from "@/components/dashboard/CountryPicker";
import StreakCard from "@/components/dashboard/StreakCard";
import AchievementsCard from "@/components/dashboard/AchievementsCard";

/**
 * 👤 ProfileTab — one friendly place for the account: who you are, the
 * daily loss budget (so budget stops never surprise you), Telegram link
 * and referrals.
 */
const ProfileTab = ({ session, isLive, balance, onConnect }) => {
  const acct = session?.account_id;
  const [budget, setBudget] = useState(null); // { budget, tripped_today }
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!acct) return;
    try {
      const res = await api.get(`/session-budget/${acct}`);
      setBudget(res.data);
      setDraft(res.data?.budget ? String(res.data.budget) : "");
    } catch { /* ignore */ }
  }, [acct]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!acct) return;
    setSaving(true);
    try {
      const val = Math.max(0, Number(draft) || 0);
      const res = await api.post(`/session-budget/${acct}`, { budget: val });
      setBudget(res.data);
      toast.success(val > 0 ? `🛑 Daily budget set: every bot stops if the day hits −$${val.toFixed(2)}` : "Daily budget turned off");
    } catch { toast.error("Could not save the budget"); }
    setSaving(false);
  };

  return (
    <div data-testid="profile-tab" className="space-y-4">
      {/* 🪪 account card */}
      <div data-testid="profile-account-card" className="rounded-lg border border-vts-line bg-vts-panel p-4 flex items-center gap-3 flex-wrap">
        <div className="w-10 h-10 rounded-full bg-vts-surface border border-vts-line flex items-center justify-center">
          <UserRound className="w-5 h-5 text-vts-silver/70" />
        </div>
        <div>
          <p className="text-sm font-bold text-white font-mono">{acct || "Not connected"}</p>
          <p className="text-[10px] font-mono text-vts-silver/50">
            {isLive ? (String(acct || "").match(/^(VRTC|VRW|DOT)/i) ? "🧪 Demo account" : "💵 Real account") : "Connect your Deriv account to unlock everything"}
            {balance?.currency ? ` · ${balance.currency}` : ""}
          </p>
        </div>
        {balance?.balance != null && (
          <span data-testid="profile-balance" className="ml-auto px-2 py-1 rounded bg-vts-surface border border-vts-line text-[12px] font-bold font-mono text-white">
            ${Number(balance.balance).toFixed(2)}
          </span>
        )}
        {!isLive && (
          <button data-testid="profile-connect" onClick={onConnect} className="ml-auto h-8 px-3 rounded-md bg-amber-500/90 hover:bg-amber-500 text-black text-[10px] font-bold font-mono uppercase">Connect</button>
        )}
      </div>

      {/* 🛑 daily loss budget */}
      <div data-testid="profile-budget-card" className="rounded-lg border border-vts-line bg-vts-panel p-4 space-y-2">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-red-400" />
          <h3 className="text-[12px] font-bold text-white font-mono">DAILY LOSS BUDGET</h3>
          {budget?.tripped_today && <span data-testid="profile-budget-tripped" className="px-1.5 py-0.5 rounded text-[9px] font-bold font-mono bg-red-500/15 text-red-300">🛑 TRIPPED TODAY — all bots were stopped when the day hit −${Number(budget.budget).toFixed(2)}</span>}
        </div>
        <p className="text-[10px] font-mono text-vts-silver/55">One hard number for the whole account: if today&apos;s combined bot losses reach it, EVERY running bot stops at once. Resets at midnight UTC. 0 = off.</p>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-mono text-vts-silver/50">−$</span>
          <input data-testid="profile-budget-input" value={draft} onChange={(e) => setDraft(e.target.value)} inputMode="decimal" placeholder="e.g. 25"
            className="h-8 w-24 rounded-md border border-vts-line bg-vts-surface px-2 text-[12px] font-mono font-bold text-white" />
          <button data-testid="profile-budget-save" onClick={save} disabled={saving || !acct}
            className="h-8 px-3 rounded-md bg-sky-500/90 hover:bg-sky-500 text-white text-[10px] font-bold font-mono uppercase disabled:opacity-40">
            {saving ? "Saving…" : "Save"}
          </button>
          {budget?.budget > 0 && !budget?.tripped_today && (
            <span className="text-[9px] font-mono text-vts-silver/50">active: stops the day at −${Number(budget.budget).toFixed(2)}</span>
          )}
        </div>
      </div>

      <CountryPicker accountId={acct} />
      <AchievementsCard accountId={acct} />
      <StreakCard accountId={acct} />
      <TelegramLinkCard accountId={isLive ? acct : null} />
      <ReferralPanel accountId={acct} onConnect={onConnect} />
    </div>
  );
};

export default ProfileTab;
